import IconButton from '@/components/Buttons/IconButton';
import WarningColored from '@/images/WarningColored.svg';
import { useRevertAgent } from '@/pages/agents/api';
import EditButton from '@/pages/agents/pages/editor/components/EditButton';
import { useClearEditor } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';

export const isCancelAgentEditModalOpen = atom({
    key: 'is-cancel-agent-edit-modal-open',
    default: false,
});

const DeleteDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        width: 452px;
        padding: 24px 30px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
        gap: 16px;
    }
`;

const DeleteText = styled.div`
    font-family: 'Libre Franklin', sans-serif;
    font-size: 16px;
    letter-spacing: 0.15px;
`;

const StyledButton = styled(Button)`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    width: fit-content;
    text-transform: none;
    border-radius: 8px;
    padding: 10px 16px;
`;

const CancelButton = styled(StyledButton)`
    color: ${theme.colors.secondary[600]};
    min-width: 192px;
    height: 39px;
    margin: 1px 8px 0 0;
`;

const Icon = styled.img``;

const TitleWithImg = styled.div`
    display: flex;
    align-items: center;
`;

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Text = styled.h2`
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
    margin: 0 0 0 10px;
`;

const DeleteCloseButton = styled(IconButton)`
    color: ${theme.colors.black[800]};
    padding: 10px;
    position: absolute;
    right: 4px;
    top: 4px;

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const CancelEditModal = observer(() => {
    const agentEditorStore = useAgentEditorStore();
    const [isOpen, setOpen] = useRecoilState(isCancelAgentEditModalOpen);

    const navigate = useNavigate();
    const { clearEditor } = useClearEditor();
    const { mutateAsync: revertAgent } = useRevertAgent();

    useEffect(() => {
        return () => {
            setOpen(false);
        };
    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    const handleLeaveClick = useCallback(async () => {
        try {
            if (agentEditorStore.isAgentEdit) {
                await revertAgent();
            }
        } finally {
            clearEditor();
            navigate(AppRoutes.Agent);
        }
    }, [clearEditor, agentEditorStore.isAgentEdit]);

    return (
        <DeleteDialog open={isOpen}>
            <Title>
                <TitleWithImg>
                    <Icon src={WarningColored} />
                    <Text>You have unsaved changes</Text>
                </TitleWithImg>
                <DeleteCloseButton ariaLabel="close" onClick={handleClose}>
                    <CloseIcon />
                </DeleteCloseButton>
            </Title>
            <DeleteText>You have unsaved changes, do you want to save them now?</DeleteText>
            <Title>
                <CancelButton onClick={handleLeaveClick} variant="outlined" color="secondary">
                    Leave
                </CancelButton>
                <EditButton />
            </Title>
        </DeleteDialog>
    );
});
